import React, { useState, useEffect } from 'react';
import { User } from '../types/User';
import { X, Calendar, Clock, MapPin, CheckCircle } from 'lucide-react';
import { MeetingService, TimeSlot } from '../services/meetingService';
import CustomNotification from './CustomNotification';

interface MeetingScheduleModalProps {
  participant: User;
  currentUser: User;
  onClose: () => void;
}

const MeetingScheduleModal: React.FC<MeetingScheduleModalProps> = ({ 
  participant, 
  currentUser, 
  onClose 
}) => {
  const [selectedDate, setSelectedDate] = useState('2026-10-06');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [location] = useState('Beogradski sajam, Hala 5 — B2B Area');
  const [description, setDescription] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [assignedTable, setAssignedTable] = useState<number | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });

  useEffect(() => {
    loadAvailableSlots();
  }, [currentUser.id, participant.id, selectedDate]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    const slots = await MeetingService.getAvailableTimeSlots(
      currentUser.id,
      participant.id,
      selectedDate
    );
    setAvailableSlots(slots);
    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTimeSlot || !subject) {
      setNotification({
        show: true,
        message: 'Please fill in all required fields',
        type: 'error'
      });
      return;
    }

    // Schedule meeting with automatic table assignment
    MeetingService.scheduleMeeting({
      requester_id: currentUser.id,
      recipient_id: participant.id,
      meeting_date: selectedDate,
      time_slot: selectedTimeSlot,
      subject,
      description,
      location
    }).then(({ success, error, tableNumber }) => {
      if (success) {
        setAssignedTable(tableNumber || 1);
        setShowSuccessPopup(true);
        // Notify parent component about the update
        onMeetingUpdate?.();
      } else {
        setNotification({
          show: true,
          message: 'Failed to send meeting request: ' + error,
          type: 'error'
        });
      }
    });
  };

  const handleCloseSuccess = () => {
    setShowSuccessPopup(false);
    onClose();
  };

  if (showSuccessPopup) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Meeting Request Sent!
            </h2>
            
            <p className="text-gray-600 mb-4">
              Your meeting request has been successfully sent to <strong>{participant.name}</strong>.
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <MapPin className="w-5 h-5" style={{ color: '#C89116' }} />
                <span className="font-semibold" style={{ color: '#C89116' }}>
                  Assigned to Table {assignedTable}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <div>📅 {selectedDate}</div>
                <div>🕐 {selectedTimeSlot}</div>
                <div>📍 {location}</div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">
              You will be notified when {participant.name} responds to your request.
            </p>
            
            <button
              onClick={handleCloseSuccess}
              className="w-full py-3 px-4 text-white rounded-lg font-semibold hover:opacity-90 transition-colors"
              style={{ backgroundColor: '#C89116' }}
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            Schedule a meeting with {participant.name}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conference Date
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                className="flex flex-col items-center justify-center w-16 h-16 text-white rounded-lg font-semibold"
                style={{ backgroundColor: '#C89116' }}
              >
                <span className="text-xs">OCTOBER</span>
                <span className="text-lg">6</span>
              </button>
            </div>
          </div>

          {/* Time Slot Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select available time slot (13:30 - 15:30)
            </label>
            {loading ? (
              <div className="p-3 text-center text-gray-500">Loading available slots...</div>
            ) : availableSlots.length > 0 ? (
              <select
                value={selectedTimeSlot}
                onChange={(e) => setSelectedTimeSlot(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600"
                required
              >
                <option value="">Select a time slot</option>
                {availableSlots.map((slot) => (
                  <option key={slot.time} value={slot.time}>
                    {slot.time}
                  </option>
                ))}
              </select>
            ) : (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800">
                No available time slots. Both you and {participant.name} have conflicting meetings.
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600"
              placeholder="Meeting topic"
              required
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <div className="p-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                <span>{location}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600"
              placeholder="Additional details about the meeting..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || availableSlots.length === 0}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors flex items-center space-x-2"
              style={{ backgroundColor: '#C89116' }}
            >
              <span>Send invite</span>
              <Calendar className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>

      {/* Custom Notification */}
      {notification.show && (
        <CustomNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default MeetingScheduleModal;